import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, FileText, ExternalLink, X, Download } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import EventNavigation from '../components/EventNavigation';
import LoadingSpinner from '../components/LoadingSpinner';
import Tooltip from '../components/Tooltip';
import api from '../services/api';
import '../components/EventHome.css';
import './EventEntityCompanies.css';

const EventEntityCompanies = () => {
    const { eventNameId, eventEntityId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tableData, setTableData] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);

    useEffect(() => {
        if (eventNameId) {
            localStorage.setItem('last_event', eventNameId);
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventNameId, eventEntityId]);

    // Verification logging
    useEffect(() => {
        if (tableData) {
            setTimeout(() => {
                const body = document.body;
                const html = document.documentElement;
                const eventLayout = document.querySelector('.event-layout');
                const eventPage = document.querySelector('.event-entity-companies-page');
                const eventContent = document.querySelector('.event-content');
                const tableContainer = document.querySelector('.table-container');
                const table = document.querySelector('.companies-table');
                
                console.log('🔍 FINAL VERIFICATION:');
                console.log('viewport:', window.innerHeight, 'x', window.innerWidth);
                console.log('---');
                console.log('html scroll?', html.scrollHeight > html.clientHeight ? '❌ YES (BAD)' : '✅ NO', `(${html.scrollHeight} vs ${html.clientHeight})`);
                console.log('body scroll?', body.scrollHeight > body.clientHeight ? '❌ YES (BAD)' : '✅ NO', `(${body.scrollHeight} vs ${body.clientHeight})`);
                console.log('---');
                console.log('event-layout vertical scroll?', eventLayout?.scrollHeight > eventLayout?.offsetHeight ? '❌ YES' : '✅ NO');
                console.log('page vertical scroll?', eventPage?.scrollHeight > eventPage?.offsetHeight ? '❌ YES' : '✅ NO');
                console.log('content vertical scroll?', eventContent?.scrollHeight > eventContent?.offsetHeight ? '❌ YES' : '✅ NO');
                console.log('---');
                console.log('table-container size:', tableContainer?.offsetWidth, 'x', tableContainer?.offsetHeight);
                console.log('table-container scroll:', tableContainer?.scrollWidth, 'x', tableContainer?.scrollHeight);
                console.log('table actual size:', table?.offsetWidth, 'x', table?.offsetHeight);
                console.log('---');
                console.log('✅ Horizontal scroll needed?', tableContainer?.scrollWidth > tableContainer?.clientWidth ? '✅ YES (scrollbar should show)' : '❌ NO (all fits)');
                console.log('✅ Vertical scroll needed?', tableContainer?.scrollHeight > tableContainer?.clientHeight ? '✅ YES (scrollbar should show)' : '❌ NO (all fits)');
            }, 300);
        }
    }, [tableData]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch event data
            const eventResponse = await api.get(`/public/events/${eventNameId}`);
            setEvent(eventResponse.data);

            // Fetch companies with profile data for this event entity
            const tableResponse = await api.get(`/event-entity-companies/${eventEntityId}/profile`);
            setTableData(tableResponse.data);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.response?.data?.detail || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (company) => {
        setSelectedCompany(company);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setTimeout(() => setSelectedCompany(null), 300); // Wait for animation
    };

    const renderCellValue = (value) => {
        if (!value) {
            return <span className="empty-cell">—</span>;
        }

        // Handle file type
        if (typeof value === 'object' && value.type === 'file') {
            return (
                <a 
                    href={value.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="file-link"
                    onClick={(e) => e.stopPropagation()}
                >
                    <ExternalLink size={14} />
                    View File
                </a>
            );
        }

        // Handle hierarchical structured data - convert to summary string for table
        if (typeof value === 'object' && value.type === 'hierarchical' && value.items) {
            const categories = value.items.map(item => item.category).join(', ');
            return <span className="cell-value">{categories}</span>;
        }

        // Handle regular values
        return <span className="cell-value">{value}</span>;
    };

    const renderDrawerValue = (value, question) => {
        if (!value) {
            return <span className="drawer-empty-value">Not provided</span>;
        }

        // Handle file type
        if (typeof value === 'object' && value !== null && value.type === 'file' && value.url) {
            const isImage = value.url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            return (
                <div className="drawer-file-section">
                    {isImage ? (
                        <div className="drawer-image-preview">
                            <img src={value.url} alt={question.question_text} />
                        </div>
                    ) : null}
                    <a 
                        href={value.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="drawer-file-link"
                    >
                        <Download size={16} />
                        Download File
                    </a>
                </div>
            );
        }

        // Handle hierarchical structured data from backend
        if (typeof value === 'object' && value !== null && value.type === 'hierarchical' && Array.isArray(value.items)) {
            console.log('Rendering hierarchical data:', value); // Debug log
            return (
                <div className="drawer-hierarchical-list">
                    {value.items.map((item, idx) => (
                        <div key={idx} className="drawer-category-group">
                            <div className="drawer-category-main">{item.category}</div>
                            {item.subcategories && item.subcategories.length > 0 && (
                                <>
                                    {item.subcategories.map((sub, subIdx) => (
                                        <div key={subIdx} className="drawer-category-sub">
                                            {sub}
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    ))}
                </div>
            );
        }

        // Handle regular text values (preserve line breaks)
        console.log('Rendering as text, value type:', typeof value, 'value:', value); // Debug log
        const textValue = String(value);
        return (
            <div className="drawer-text-value">
                {textValue.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                        {line}
                        {i < textValue.split('\n').length - 1 && <br />}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <LoadingSpinner message="Loading companies..." />
                    </div>
                </div>
            </>
        );
    }

    if (error || !event) {
        return (
            <>
                <NavigationBar />
                <div className="event-layout">
                    <EventNavigation eventNameId={eventNameId} />
                    <div className="event-home-container with-sidebar">
                        <div className="error-message">
                            <h2>Error</h2>
                            <p>{error || 'Event not found'}</p>
                            <button onClick={() => navigate(`/event/${eventNameId}/home`)} className="btn btn-primary">
                                Return to Event Home
                            </button>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <NavigationBar />
            <div className="event-layout">
                <EventNavigation eventNameId={eventNameId} />
                <div className="event-home-container with-sidebar event-entity-companies-page">
                    <div className="event-content">
                        <div className="page-header">
                            <h1>
                                {tableData?.event_entity_name || 'Event Entity'} ({tableData?.total || 0})
                            </h1>
                        </div>

                        {!tableData || tableData.companies.length === 0 ? (
                            <div className="empty-state">
                                <Building2 size={48} />
                                <h3>No Companies Registered</h3>
                                <p>There are no companies registered for this event entity yet.</p>
                            </div>
                        ) : tableData.questions.length === 0 ? (
                            <div className="empty-state">
                                <FileText size={48} />
                                <h3>No Form Questions</h3>
                                <p>No entity profile form questions have been configured yet.</p>
                                <div className="companies-without-form">
                                    <h4>Registered Companies:</h4>
                                    <ul>
                                        {tableData.companies.map((company) => (
                                            <li key={company.CompanyId}>{company.CompanyName}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : (
                            <div className="table-container">
                                <table className="companies-table">
                                    <thead>
                                        <tr>
                                            <th className="company-name-header">
                                                <Tooltip text="Organization Name">
                                                    Organization Name
                                                </Tooltip>
                                            </th>
                                            {tableData.questions.map((question) => {
                                                const displayText = question.short_description || question.question_text;
                                                const tooltipText = question.short_description 
                                                    ? `${question.short_description}\n\nFull Question: ${question.question_text}`
                                                    : question.question_text;
                                                return (
                                                    <th 
                                                        key={question.question_id} 
                                                        className="question-header"
                                                    >
                                                        <Tooltip text={tooltipText} multiline={!!question.short_description}>
                                                            {displayText}
                                                        </Tooltip>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.companies.map((company) => (
                                            <tr 
                                                key={company.EventCompanyId}
                                                onClick={() => handleRowClick(company)}
                                                className="clickable-row"
                                            >
                                                <td className="company-name-cell">
                                                    {company.CompanyName}
                                                </td>
                                                {tableData.questions.map((question) => (
                                                    <td key={question.question_id} className="answer-cell">
                                                        {renderCellValue(company.answers[question.question_id])}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Profile Drawer */}
            {drawerOpen && (
                <>
                    <div className="drawer-overlay" onClick={closeDrawer} />
                    <div className={`profile-drawer ${drawerOpen ? 'open' : ''}`}>
                        <div className="drawer-header">
                            <div className="drawer-title-section">
                                <span className="drawer-entity-tag">{tableData?.event_entity_name}</span>
                                <h2>{selectedCompany?.CompanyName}</h2>
                                
                                {/* Display contacts */}
                                {selectedCompany?.contacts && selectedCompany.contacts.length > 0 && (
                                    <div className="drawer-contacts">
                                        {selectedCompany.contacts.map((contact) => (
                                            <div key={contact.ContactId} className="drawer-contact-item">
                                                {contact.ImageUrl && (
                                                    <img 
                                                        src={contact.ImageUrl} 
                                                        alt={`${contact.FirstName} ${contact.LastName}`}
                                                        className="drawer-contact-avatar"
                                                    />
                                                )}
                                                {!contact.ImageUrl && (
                                                    <div className="drawer-contact-avatar-placeholder">
                                                        {contact.FirstName?.[0]}{contact.LastName?.[0]}
                                                    </div>
                                                )}
                                                <div className="drawer-contact-info">
                                                    <div className="drawer-contact-name">
                                                        {contact.FirstName} {contact.LastName}
                                                    </div>
                                                    {contact.JobTitle && (
                                                        <div className="drawer-contact-title">
                                                            {contact.JobTitle}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button className="drawer-close-btn" onClick={closeDrawer} aria-label="Close drawer">
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div className="drawer-content">
                            {tableData?.questions
                                .filter((question) => {
                                    const value = selectedCompany?.answers[question.question_id];
                                    return value !== null && value !== undefined && value !== '';
                                })
                                .map((question) => {
                                    const value = selectedCompany?.answers[question.question_id];
                                    
                                    return (
                                        <div key={question.question_id} className="drawer-field">
                                            <label className="drawer-field-label">
                                                {question.short_description || question.question_text}
                                            </label>
                                            <div className="drawer-field-value">
                                                {renderDrawerValue(value, question)}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                        
                        {/* Sticky Meet Button */}
                        <div className="drawer-footer">
                            <button className="drawer-meet-btn">
                                Meet
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default EventEntityCompanies;
